import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';
import { api } from '../../common';
import { ClientDiagnosticsService, SKIP_CLIENT_DIAGNOSTICS } from '../services/client-diagnostics.service';
import { recordRumEvent } from '../monitoring/aws-rum';

const CUSTOMERS_REP_PATH = '/customers/rep';

export const clientDiagnosticsInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(SKIP_CLIENT_DIAGNOSTICS) || !req.url.startsWith(api)) {
    return next(req);
  }

  const diagnostics = inject(ClientDiagnosticsService);
  const path = diagnostics.toApiPath(req.url);
  const isCustomersRepRequest = path === CUSTOMERS_REP_PATH;

  if (isCustomersRepRequest) {
    diagnostics.trackApi(
      'api.customers_rep.request.started',
      {
        method: req.method,
        path,
      },
      'info',
    );
  }

  return next(req).pipe(
    tap({
      next: (event) => {
        if (!(event instanceof HttpResponse)) {
          return;
        }

        const requestId = event.headers.get('x-request-id') || undefined;

        if (isCustomersRepRequest) {
          const count = Array.isArray(event.body) ? event.body.length : undefined;
          diagnostics.trackApi(
            count === 0 ? 'api.customers_rep.response.empty' : 'api.customers_rep.response.success',
            {
              method: req.method,
              path,
              status: event.status,
              requestId,
              count,
            },
            count === 0 ? 'warn' : 'info',
          );
          return;
        }

        if (event.status >= 500) {
          diagnostics.trackApi(
            'api.response.server_error',
            {
              method: req.method,
              path,
              status: event.status,
              requestId,
            },
            'error',
          );
        }
      },
      error: (error: HttpErrorResponse) => {
        const requestId = error.headers?.get('x-request-id') || undefined;
        const level = error.status >= 500 ? 'error' : 'warn';

        if (error.status === 401 || error.status === 403) {
          recordRumEvent('auth.api_error', {
            app: 'rep-flow',
            method: req.method,
            path,
            status: error.status,
            requestId,
            message: error.error?.message || error.message,
          });
        }

        if (isCustomersRepRequest) {
          diagnostics.trackApi(
            'api.customers_rep.response.error',
            {
              method: req.method,
              path,
              status: error.status,
              requestId,
              message: error.error?.message || error.message,
            },
            level,
          );
          return;
        }

        if (error.status === 401 || error.status === 403 || error.status >= 500) {
          diagnostics.trackApi(
            'api.response.error',
            {
              method: req.method,
              path,
              status: error.status,
              requestId,
              message: error.error?.message || error.message,
            },
            level,
          );
        }
      },
    }),
  );
};
